import { Link } from "react-router-dom";
import { ArrowRight, FileText, Coins, Percent, Compass, Leaf, Factory, Building2, GraduationCap, Rocket, Cog, Landmark, Lightbulb, Info } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/lib/seo";

const IMG_WAVE =
    "https://images.unsplash.com/photo-1712397943847-e104395a1a8b?auto=format&fit=crop&w=1800&q=80";

const DEFINITIONS = [
    {
        icon: FileText,
        t: "BANDI",
        d: "Strumenti attraverso i quali enti pubblici ed europei mettono a disposizione risorse destinate a obiettivi specifici: sviluppo, innovazione, digitalizzazione e altro.",
        testid: "bandi-card-bandi",
    },
    {
        icon: Coins,
        t: "CONTRIBUTI",
        d: "Sostegni economici che possono essere erogati secondo le regole di ciascun bando, a supporto di progetti e investimenti delle imprese.",
        testid: "bandi-card-contributi",
    },
    {
        icon: Percent,
        t: "AGEVOLAZIONI",
        d: "Condizioni favorevoli — come tassi ridotti o garanzie pubbliche — pensate per rendere più accessibili gli investimenti delle attività.",
        testid: "bandi-card-agevolazioni",
    },
    {
        icon: Compass,
        t: "OPPORTUNITÀ DI FINANZIAMENTO",
        d: "Le occasioni concrete di accesso a risorse, che cambiano nel tempo in base a scadenze, disponibilità e requisiti richiesti.",
        testid: "bandi-card-opportunita",
    },
];

const MEASURES = [
    {
        slug: "greentour",
        icon: Leaf,
        tag: "TURISMO",
        title: "GreenTour",
        desc: "Sostegno ai piccoli insediamenti turistici per progetti di investimento che innovano l'offerta con attenzione all'impatto ambientale e alla valorizzazione del territorio.",
    },
    {
        slug: "investimenti-sostenibili-40",
        icon: Factory,
        tag: "MANIFATTURA",
        title: "Investimenti sostenibili 4.0",
        desc: "Contributi a favore di progetti di investimento delle imprese manifatturiere orientati all'innovazione, alla digitalizzazione e alla sostenibilità ambientale.",
    },
    {
        slug: "fondo-deindustrializzazione-2026",
        icon: Building2,
        tag: "CONTINUITÀ INDUSTRIALE",
        title: "Fondo di contrasto alla deindustrializzazione 2026",
        desc: "Strumenti di sostegno per le aree interessate da fenomeni di deindustrializzazione, a favore di progetti che assicurino continuità e rilancio dell'attività produttiva.",
    },
    {
        slug: "sviluppo-competenze-pmi",
        icon: GraduationCap,
        tag: "PMI",
        title: "Sviluppo competenze specialistiche PMI",
        desc: "Contributo per l'acquisto di servizi di consulenza specialistica a supporto dei processi di crescita, digitalizzazione e trasformazione delle piccole e medie imprese.",
    },
    {
        slug: "resto-al-sud-20",
        icon: Rocket,
        tag: "IMPRENDITORIA GIOVANILE",
        title: "Resto al Sud 2.0",
        desc: "Misura dedicata alla nascita di nuove attività nel Mezzogiorno: sostegno a fondo perduto alle spese di start-up e di gestione per i giovani che vogliono avviare un'impresa.",
    },
    {
        slug: "nuova-sabatini",
        icon: Cog,
        tag: "MACCHINARI E TECNOLOGIA",
        title: "Nuova Sabatini",
        desc: "Contributo in conto interessi sui finanziamenti destinati all'acquisto di macchinari, attrezzature, impianti e beni in locazione finanziaria, anche digitali.",
    },
    {
        slug: "cultura-cresce",
        icon: Landmark,
        tag: "CULTURA E TURISMO",
        title: "Cultura Cresce",
        desc: "Sostegno a progetti di investimento che innovano l'offerta culturale e turistica e valorizzano il patrimonio e le tradizioni del territorio.",
    },
    {
        slug: "smart-start-italia",
        icon: Lightbulb,
        tag: "START-UP",
        title: "Smart&Start Italia",
        desc: "Finanziamento a tasso zero a sostegno delle start-up innovative, con un contributo aggiuntivo a fondo perduto per i fondatori più giovani e per le imprese femminili.",
    },
];

export default function Bandi() {
    useSEO(
        "Bandi e Finanza Agevolata | Service & Business SRLS",
        "Service & Business SRLS affianca imprese e professionisti nell'individuazione e nella gestione delle opportunità di bandi, contributi e finanza agevolata."
    );

    return (
        <>
            <PageHero
                eyebrow="Bandi & Finanza agevolata"
                lines={["Bandi e finanza agevolata", "per sostenere lo sviluppo", "della tua impresa."]}
                sub="Un supporto per orientarti tra bandi, contributi, agevolazioni e strumenti disponibili per le imprese."
            />

            <section className="pb-4">
                <div className="wrap">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-3xl border border-white/10">
                            <img
                                src={IMG_WAVE}
                                alt="Onde di luce azzurra su fondo scuro, richiamo visivo al gradiente del marchio"
                                className="w-full h-52 sm:h-72 object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,15,23,0.85) 0%, rgba(11,15,23,0.35) 60%, rgba(11,15,23,0.1) 100%)" }} />
                            <p className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 font-display text-lg sm:text-xl font-semibold text-white max-w-md leading-snug">
                                Ogni bando ha i suoi tempi, i suoi requisiti e le sue regole.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-20 sm:py-24">
                <div className="wrap">
                    <Reveal>
                        <p className="eyebrow mb-6">In sintesi</p>
                        <h2 className="h2 max-w-3xl">Di cosa parliamo, in parole semplici.</h2>
                    </Reveal>
                    <div className="mt-12 grid sm:grid-cols-2 gap-6">
                        {DEFINITIONS.map((d, i) => (
                            <Reveal key={d.t} delay={i * 0.07} className="h-full">
                                <div className="card-dark rounded-2xl p-8 h-full transition-all duration-300 hover:border-sky-400/40" data-testid={d.testid}>
                                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-sky-400/30 bg-sky-400/5">
                                        <d.icon className="h-5 w-5 text-sky-400" />
                                    </span>
                                    <h3 className="font-display text-base font-bold tracking-[0.12em] text-white mt-6">{d.t}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed mt-3">{d.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="pb-8">
                <div className="wrap">
                    <Reveal>
                        <div className="flex flex-wrap items-center gap-4 justify-between">
                            <h2 className="h2">Misure in evidenza</h2>
                            <span className="font-mono text-[10px] tracking-[0.24em] text-sky-400 border border-sky-400/30 rounded-full px-4 py-2">
                                AGGIORNAMENTO CONTINUO
                            </span>
                        </div>
                        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {MEASURES.map((m, i) => (
                                <Reveal key={m.slug} delay={i * 0.05} className="h-full">
                                    <article
                                        className="card-dark rounded-2xl p-7 h-full flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-sky-400/40"
                                        data-testid={`bando-card-${m.slug}`}
                                    >
                                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-sky-400/30 bg-sky-400/5">
                                            <m.icon className="h-5 w-5 text-sky-400" />
                                        </span>
                                        <p className="font-mono text-[9px] tracking-[0.24em] text-sky-400 uppercase mt-6">{m.tag}</p>
                                        <h3 className="font-display text-base font-bold text-white leading-snug mt-2">{m.title}</h3>
                                        <p className="text-sm text-slate-400 leading-relaxed mt-3 flex-1">{m.desc}</p>
                                    </article>
                                </Reveal>
                            ))}
                        </div>
                        <p className="flex items-start gap-2.5 text-xs text-slate-500 leading-relaxed mt-8 max-w-3xl">
                            <Info className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" />
                            Descrizioni a puro titolo orientativo: per ogni misura valgono i requisiti, le scadenze e le
                            condizioni indicate nei relativi bandi ufficiali. L'ottenimento di contributi o finanziamenti
                            dipende dai requisiti previsti e dalle valutazioni degli enti erogatori.
                        </p>
                    </Reveal>
                </div>
            </section>

            <CTASection
                title="Vuoi sapere se esistono opportunità per la tua attività?"
                text="Richiedi un primo contatto: valutiamo insieme la tua situazione."
                ctaLabel="Richiedi un primo contatto"
                testid="bandi-final-cta"
            />
        </>
    );
}
